import { useQuery, useQueryClient } from "@tanstack/react-query";
import { NavLink, Route, Routes, useParams } from "react-router-dom";
import { api } from "./lib/api";
import { useWsStatus } from "./lib/ws";
import { Apks } from "./pages/Apks";
import { Board } from "./pages/Board";
import { Costs } from "./pages/Costs";
import { Requests } from "./pages/Requests";
import { DeviceLive } from "./pages/DeviceLive";
import { BuildDetail, Builds } from "./pages/Builds";
import { Feedback } from "./pages/Feedback";
import { Devices } from "./pages/Devices";
import { Events } from "./pages/Events";
import { Feeds } from "./pages/Feeds";
import { GraphView } from "./pages/GraphView";
import { Login } from "./pages/Login";
import { ModelQueries } from "./pages/ModelQueries";
import { RunInspector } from "./pages/RunInspector";

function DeviceNav() {
  const { deviceId } = useParams();
  if (!deviceId) return null;
  const base = `/d/${deviceId}`;
  return (
    <>
      <span className="nav-sep">/</span>
      <NavLink to={base} end>
        live
      </NavLink>
      <NavLink to={`${base}/graph`}>graph</NavLink>
      <NavLink to={`${base}/feeds`}>feeds</NavLink>
      <NavLink to={`${base}/events`}>events</NavLink>
      <NavLink to={`${base}/model-queries`}>model</NavLink>
    </>
  );
}

function Shell({ user, authDisabled }: { user: string | null; authDisabled: boolean }) {
  const ws = useWsStatus();
  const qc = useQueryClient();
  const logout = async () => {
    await api.logout();
    qc.clear();
    location.assign("/login");
  };
  return (
    <div className="app">
      <header className="topbar">
        <NavLink to="/" className="brand">
          Facade Inspector
        </NavLink>
        <nav>
          <Routes>
            <Route path="/d/:deviceId/*" element={<DeviceNav />} />
            <Route path="*" element={null} />
          </Routes>
          <span className="nav-sep">·</span>
          <NavLink to="/apks">releases</NavLink>
          <NavLink to="/board">board</NavLink>
          <NavLink to="/requests">requests</NavLink>
          <NavLink to="/builds">builds</NavLink>
          <NavLink to="/feedback">feedback</NavLink>
          <NavLink to="/costs">costs</NavLink>
        </nav>
        <span className={`ws-dot ${ws}`} title={`browser socket: ${ws}`}>
          {ws}
        </span>
        {authDisabled ? (
          <span className="auth-badge" title="AUTH_DISABLED=1 — dev mode, no login">
            auth off
          </span>
        ) : (
          <button className="small" onClick={logout} title={`signed in as ${user ?? "?"}`}>
            logout
          </button>
        )}
      </header>
      <main>
        <Routes>
          <Route path="/" element={<Devices />} />
          <Route path="/login" element={<Devices />} />
          <Route path="/d/:deviceId" element={<DeviceLive />} />
          <Route path="/d/:deviceId/runs/:runId" element={<RunInspector />} />
          <Route path="/d/:deviceId/graph" element={<GraphView />} />
          <Route path="/d/:deviceId/feeds" element={<Feeds />} />
          <Route path="/d/:deviceId/events" element={<Events />} />
          <Route path="/d/:deviceId/model-queries" element={<ModelQueries />} />
          <Route path="/model-queries" element={<ModelQueries />} />
          <Route path="/apks" element={<Apks />} />
          <Route path="/feedback" element={<Feedback />} />
          <Route path="/builds" element={<Builds />} />
          <Route path="/builds/:buildId" element={<BuildDetail />} />
          <Route path="/board" element={<Board />} />
          <Route path="/requests" element={<Requests />} />
          <Route path="/costs" element={<Costs />} />
        </Routes>
      </main>
    </div>
  );
}

export function App() {
  // Who am I? Decides between the login page and the app; the browser socket is only opened once
  // we know there is a session (it would be closed with 4401 otherwise).
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, staleTime: 60_000, retry: false });
  if (me.isLoading) return <div className="login-wrap muted">loading…</div>;
  if (me.isError) return <div className="login-wrap muted">server unreachable — {(me.error as Error).message}</div>;
  const { authenticated, user, authDisabled } = me.data!;
  if (!authenticated && !authDisabled) return <Login onLoggedIn={() => me.refetch()} />;
  return <Shell user={user} authDisabled={authDisabled} />;
}
